import React from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Award, Trophy, Newspaper, Lightbulb, Calendar } from 'lucide-react';
import { awards, mentions, initiatives } from '../mock/mockData';

const Awards = () => {
  return (
    <div className="relative z-10 min-h-screen pt-24 pb-16 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">Awards & Recognition</h1>
          <p className="text-xl text-gray-300">Achievements, Media Mentions & Industry Impact</p>
          <div className="h-1 w-32 bg-gradient-to-r from-blue-600 to-sky-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Awards Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Trophy className="h-8 w-8 text-sky-400 mr-3" />
            Major Awards
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {awards.map((award) => (
              <Card
                key={award.id}
                className="p-8 border-blue-900/30 shadow-xl bg-gradient-to-br from-blue-950/40 to-sky-950/40 backdrop-blur hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-sky-500 rounded-full">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <Badge className="bg-[#060f1e]/80 text-sky-300 border-0">
                    {award.year}
                  </Badge>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{award.title}</h3>
                <p className="text-sky-300 font-semibold mb-3">{award.issuer}</p>
                <p className="text-gray-200">{award.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Initiatives Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Lightbulb className="h-8 w-8 text-blue-400 mr-3" />
            Key Initiatives & Programs
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {initiatives.map((initiative) => (
              <Card
                key={initiative.id}
                className="p-6 border-blue-900/30 shadow-lg bg-[#060f1e]/80 backdrop-blur hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-900/30 rounded-full">
                    <Lightbulb className="h-6 w-6 text-blue-400" />
                  </div>
                  <Badge className="bg-blue-900/30 text-sky-300 border-0">
                    {initiative.year}
                  </Badge>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{initiative.title}</h3>
                <p className="text-gray-200">{initiative.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Media Mentions Section */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Newspaper className="h-8 w-8 text-sky-400 mr-3" />
            Media Mentions & Publications
          </h2>
          <Card className="p-8 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur">
            <p className="text-gray-200 mb-6">Featured in leading regional and international media outlets:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {mentions.map((mention) => (
                <div
                  key={mention.id}
                  className="flex items-center justify-center p-4 bg-gradient-to-br from-blue-950/40 to-sky-950/40 rounded-lg hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 hover:scale-105"
                >
                  <div className="text-center">
                    <Newspaper className="h-6 w-6 text-sky-400 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-white">{mention.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Speaking Engagements */}
          <Card className="p-8 border-blue-900/30 shadow-xl bg-gradient-to-br from-blue-950/40 to-sky-950/40 backdrop-blur mt-6">
            <div className="text-center">
              <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-sky-500 rounded-full mx-auto mb-4">
                <Trophy className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">100+ Keynote Speeches</h3>
              <p className="text-lg text-gray-200">Delivered across the UAE since 2013</p>
              <p className="text-gray-300 mt-4">Thought leadership in renewable energy, sustainability, and innovation</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Awards;